import React, { useMemo } from 'react';
import { Card, Col, Row, Space, Typography, Table } from 'antd';
import { useTickerData } from "../../../hooks/useTickerData";
import { stringMixin, money } from "../../../utils/strings";
import TeeterTotterChart from "../components/TeeterTotterChart";

const validBenchmarks = ["agg", "qqq", "spy", "vtv", "bnd", "eem", "efa", "iwm", "iwr", "vxus"];
const statFieldConfig = ['stats.windows.1_yr'];

// Field type mappings
const FIELD_FORMATS = {
    // Percentage fields (common ratios and statistics)
    PERCENTAGE_FIELDS: [
        'alpha_1_yr', 'alpha_3_yr', 'alpha_5_yr',
        'batting_average',
        'downside_capture_1_yr', 'downside_capture_3_yr', 'downside_capture_5_yr',
        'upside_capture_1_yr', 'upside_capture_3_yr', 'upside_capture_5_yr',
        'roc', 'volatility',
        'avg_dd', 'max_dd',
        'drawdown_deviation',
        'pain_index',
        'skew_mom',
        'mean_mom',
        'var_mom'
    ],

    // Currency/Money fields (typically prices and values)
    CURRENCY_FIELDS: [
        'max_px',
        'min_px',
        'vwap',
        'vx_mean',
        'vx_momentum'
    ],

    // Pure number fields (ratios and other metrics that should be formatted to 3 decimals)
    NUMBER_FIELDS: [
        'beta_1_yr', 'beta_3_yr', 'beta_5_yr',
        'corr_1_yr', 'corr_3_yr', 'corr_5_yr',
        'r2_1_yr', 'r2_3_yr', 'r2_5_yr',
        'sharpe_ratio_1_yr', 'sharpe_ratio_3_yr', 'sharpe_ratio_5_yr',
        'sortino_ratio_1_yr', 'sortino_ratio_3_yr', 'sortino_ratio_5_yr',
        'burke_ratio',
        'gain_loss_ratio',
        'omega_ratio',
        'sterling_ratio',
        'stutzer_index',
        'ulcer_index',
        'kappa_1ar', 'kappa_3ar', 'kappa_5ar',
        'kurt_mom',
        'total_dd_count',
        'avg_dd_duration'
    ],

    DAYS_FIELDS: [
        'avg_dd_duration',

    ]
};

const formatValue = (value, fieldName) => {
    if (typeof value !== 'number') return value;

    if (FIELD_FORMATS.PERCENTAGE_FIELDS.includes(fieldName)) {
        return stringMixin.pct(value);
    }

    if (FIELD_FORMATS.CURRENCY_FIELDS.includes(fieldName)) {
        return money(value, 0);
    }

    if (FIELD_FORMATS.NUMBER_FIELDS.includes(fieldName)) {
        return value.toFixed(3);
    }

    if (FIELD_FORMATS.DAYS_FIELDS.includes(fieldName)) {
        return `${value.toFixed(1)} Days`
    }

    // Default format for unspecified fields
    return value.toFixed(3);
};



const StatsView = ({ portfolioId, benchmark }) => {
    const benchmarkSymbol = validBenchmarks.includes(benchmark) ? benchmark.toLowerCase() : 'spy';
    const fields = [
        ...statFieldConfig,
        `stats.benchmarks.${benchmarkSymbol}`,
    ];
    const symbols = [portfolioId, benchmarkSymbol];

    const tickerData = useTickerData(symbols, fields, 'stats');

    const windowComparisonColumns = [
        {
            title: 'Metric',
            dataIndex: 'metric',
            key: 'metric',
        },
        {
            title: 'Portfolio',
            dataIndex: 'portfolio',
            key: 'portfolio',
            render: (value, record) => formatValue(value, record.key),
        },
        {
            title: benchmarkSymbol?.toUpperCase(),
            dataIndex: 'benchmark',
            key: 'benchmark',
            render: (value, record) => formatValue(value, record.key),
        },
        {
            title: 'Comparison',
            dataIndex: 'sparkline',
            key: 'sparkline',
            width: '35%',
            render: (_, record) =>
                // SPARKLINE_METRICS.includes(record.key) ? (
                    <TeeterTotterChart
                        portfolio={record.portfolio}
                        benchmark={record.benchmark}
                        fieldName={record.key}
                    />
                // ) : null,
        }

    ];

    const benchmarkStatsColumns = [
        {
            title: 'Metric',
            dataIndex: 'metric',
            key: 'metric',
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            render: (value, record) => formatValue(value, record.key),
        }
    ];

    const windowComparisonData = useMemo(() => {
        if (!tickerData || !tickerData[portfolioId] || !tickerData[benchmarkSymbol]) return [];

        const portfolioStats = tickerData?.[portfolioId]?.stats?.windows?.['1_yr'];
        const benchmarkStats = tickerData?.[benchmarkSymbol]?.stats?.windows?.['1_yr'];
        if (!portfolioStats || !benchmarkStats) return [];
        return Object.keys(portfolioStats)
            .filter(key => typeof portfolioStats[key] === 'number')
            .map(key => ({
                key,
                metric: key.replace(/_/g, ' ').toUpperCase(),
                portfolio: portfolioStats[key],
                benchmark: benchmarkStats[key],
            }));
    }, [tickerData, portfolioId, benchmarkSymbol]);

    const benchmarkStatsData = useMemo(() => {
        if (!tickerData || !tickerData[portfolioId]?.stats?.benchmarks?.[benchmarkSymbol]) return [];

        const benchmarkStats = tickerData[portfolioId].stats.benchmarks[benchmarkSymbol];

        return Object.entries(benchmarkStats)
            .filter(([key, value]) => typeof value === 'number')
            .map(([key, value]) => ({
                key,
                metric: key.replace(/_/g, ' ').toUpperCase(),
                value,
            }));
    }, [tickerData, portfolioId, benchmarkSymbol]);

    if (!tickerData) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <Row gutter={[32, 32]} justify={'center'}>
                <Col flex={'0 0 400px'}>
                    <Card bordered={false}
                          title={<Typography.Text strong>Metrics Against Benchmark</Typography.Text>}
                    >
                        <Table
                            columns={benchmarkStatsColumns}
                            dataSource={benchmarkStatsData}
                            pagination={false}
                            scroll={{y: '80vh'}}

                        />
                    </Card>
                </Col>

                <Col flex={'0 0 800px'}>
                    <Card bordered={false}
                          title={<Typography.Text strong>Stats (1 Year)</Typography.Text>}
                    >
                        <Table
                            columns={windowComparisonColumns}
                            dataSource={windowComparisonData}
                            pagination={false}
                            scroll={{y: '80vh'}}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default StatsView;